"use client";

import { ChevronDown } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

type FilterType = "popular" | "new" | "following" | "week";

interface WeekOption {
  label: string;
  dateRange: string;
}

interface FilterBarProps {
  currentFilter?: FilterType;
  onFilterChange?: (filter: FilterType) => void;
  weekLabel?: string;
  weekOptions?: WeekOption[];
  selectedWeekIndex?: number;
  onWeekSelect?: (index: number) => void;
}

export function FilterBar({
  currentFilter = "popular",
  onFilterChange,
  weekLabel = "5-12 ноября",
  weekOptions = [],
  selectedWeekIndex = 0,
  onWeekSelect,
}: FilterBarProps) {
  const handleFilterClick = (filter: FilterType) => {
    onFilterChange?.(filter);
  };

  const filters = [
    { id: "popular" as FilterType, label: "Популярные" },
    { id: "new" as FilterType, label: "Новые" },
    { id: "following" as FilterType, label: "Подписки" },
  ];

  return (
    <div className="sticky top-16 z-40 bg-background border-b border-border">
      <div className="container max-w-7xl mx-auto px-4 md:px-6">
        <div className="flex items-center h-12">
          {/* Filter Tabs — scrollable on mobile */}
          <div className="flex items-center gap-1 overflow-x-auto scrollbar-hide -mx-4 px-4 md:mx-0 md:px-0">
          {filters.map((filter) => (
            <button
              key={filter.id}
              onClick={() => handleFilterClick(filter.id)}
              className={cn(
                "shrink-0 px-3 sm:px-4 py-1.5 text-sm font-medium rounded-full transition-colors whitespace-nowrap",
                currentFilter === filter.id
                  ? "bg-primary-subtle text-primary-text hover:bg-primary-subtle-hover"
                  : "text-muted-foreground hover:text-foreground hover:bg-accent/50"
              )}
            >
              {filter.label}
            </button>
          ))}

          {/* Week Filter with Dropdown */}
          <DropdownMenu modal={false}>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                className={cn(
                  "shrink-0 gap-1 px-3 sm:px-4 rounded-full whitespace-nowrap focus-visible:ring-0 focus-visible:border-transparent",
                  currentFilter === "week"
                    ? "bg-primary-subtle text-primary-text hover:bg-primary-subtle-hover hover:text-primary-text"
                    : "text-muted-foreground hover:text-foreground hover:bg-accent/50"
                )}
              >
                <span>Неделя: {weekOptions.length > 0 && currentFilter === "week" ? weekOptions[selectedWeekIndex]?.dateRange ?? weekLabel : weekLabel}</span>
                <ChevronDown className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-56">
              {weekOptions.length > 0 ? weekOptions.map((option, index) => (
                <DropdownMenuItem
                  key={index}
                  onClick={() => {
                    handleFilterClick("week");
                    onWeekSelect?.(index);
                  }}
                >
                  <div className="flex flex-col">
                    <span className={cn("font-medium", selectedWeekIndex === index && currentFilter === "week" && "text-primary-text")}>{option.label}</span>
                    <span className="text-xs text-muted-foreground">
                      {option.dateRange}
                    </span>
                  </div>
                </DropdownMenuItem>
              )) : (
                <DropdownMenuItem onClick={() => handleFilterClick("week")}>
                  <div className="flex flex-col">
                    <span className="font-medium">Эта неделя</span>
                    <span className="text-xs text-muted-foreground">
                      {weekLabel}
                    </span>
                  </div>
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        </div>
      </div>
    </div>
  );
}
